import type { DashboardSummary, HousekeepingTaskSummary, RoomServiceTaskSummary } from "@apartos/contracts";

export type DashboardTask = DashboardSummary["attention"][number];
export type DashboardTaskSort = "newest" | "urgent" | "apartment";
export type HousekeepingTaskSort = "newest" | "priority" | "due";

const time = (value?: string) => value ? new Date(value).getTime() || 0 : 0;
const toneRank: Record<DashboardTask["tone"], number> = { red: 0, orange: 1, yellow: 2 };
const priorityRank: Record<HousekeepingTaskSummary["priority"], number> = { urgent: 0, high: 1, normal: 2 };

export function compareDashboardTasks(sort: DashboardTaskSort) {
  return (left: DashboardTask, right: DashboardTask): number => {
    if (sort === "urgent") {
      const urgency = toneRank[left.tone] - toneRank[right.tone];
      if (urgency) return urgency;
    }
    if (sort === "apartment") {
      const apartment = (left.apartmentNumber ?? "").localeCompare(right.apartmentNumber ?? "", "ru", { numeric: true });
      if (apartment) return apartment;
    }
    return time(right.createdAt) - time(left.createdAt) || right.id.localeCompare(left.id);
  };
}

export function compareHousekeepingTasks(sort: HousekeepingTaskSort) {
  return (left: HousekeepingTaskSummary, right: HousekeepingTaskSummary): number => {
    const completion = Number(left.status === "completed") - Number(right.status === "completed");
    if (completion) return completion;
    if (sort === "priority") {
      const priority = priorityRank[left.priority] - priorityRank[right.priority];
      if (priority) return priority;
    }
    if (sort === "due") {
      const due = time(left.dueAt) - time(right.dueAt);
      if (due) return due;
    }
    return time(right.createdAt) - time(left.createdAt) || right.id.localeCompare(left.id);
  };
}

export function compareRoomServiceTasks(left: RoomServiceTaskSummary, right: RoomServiceTaskSummary): number {
  const finished = (item: RoomServiceTaskSummary) => Number(item.status === "completed" || item.status === "cancelled");
  const completion = finished(left) - finished(right);
  if (completion) return completion;
  return time(right.createdAt) - time(left.createdAt) || right.id.localeCompare(left.id);
}
